import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment, List, ListItemButton,
  ListItemIcon, ListItemText, Chip, CircularProgress, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useEmpresaActiva } from '../../context/EmpresaContableContext';

// Pantalla de selección de empresa contable — equivalente al "FrmSelEmpresa" del
// sistema de escritorio: lista las empresas asignadas al usuario, con buscador
// por Razón Social o RUC, y al elegir una queda como la empresa activa de Contabilidad.
export default function SeleccionarEmpresaContable({ onSelected }) {
  const { empresas, empresaActivaId, loading, setEmpresaActiva } = useEmpresaActiva();
  const [busqueda, setBusqueda] = useState('');

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return empresas;
    return empresas.filter(e =>
      (e.razon_social || '').toLowerCase().includes(q) ||
      (e.ruc || '').toLowerCase().includes(q)
    );
  }, [empresas, busqueda]);

  const elegir = (id) => {
    setEmpresaActiva(id);
    if (onSelected) onSelected(id);
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 2 }}>
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'secondary.main', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BusinessIcon />
          <Typography variant="h6" fontWeight="bold">Empresas Contables Asignadas</Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <TextField
            fullWidth size="small" placeholder="Buscar por Razón Social o RUC..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
          ) : empresas.length === 0 ? (
            <Alert severity="info">
              No tienes ninguna empresa contable asignada todavía. Pide al administrador del sistema que te asigne una
              desde "Permisos de Usuario".
            </Alert>
          ) : filtradas.length === 0 ? (
            <Alert severity="warning">No se encontró ninguna empresa con ese criterio de búsqueda.</Alert>
          ) : (
            <List sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {filtradas.map(e => {
                const activa = String(e.id) === String(empresaActivaId);
                return (
                  <ListItemButton
                    key={e.id} onClick={() => elegir(e.id)}
                    selected={activa}
                    sx={{ borderRadius: 2, mb: 1, border: '1px solid', borderColor: activa ? 'secondary.main' : 'divider' }}
                  >
                    <ListItemIcon><BusinessIcon color={activa ? 'secondary' : 'action'} /></ListItemIcon>
                    <ListItemText
                      primary={e.razon_social}
                      secondary={`RUC: ${e.ruc}${!e.activo ? ' — Inactiva' : ''}`}
                    />
                    {activa && <Chip size="small" color="secondary" icon={<CheckCircleIcon />} label="Activa" />}
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
